import { Box, Heading, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { LoadingView } from '@resistapp/client/components/states/loading';
import { useGet } from '@resistapp/client/hooks/api';
import { AssayTmUpdatesResponse } from '@resistapp/common/api-types';
import { Feature, hasFeature } from '@resistapp/common/features';
import { format } from 'date-fns';
import { useUser } from '../../contexts/use-user-context';

function formatDate(value: string | Date) {
  const date = value instanceof Date ? value : new Date(value);
  return format(date, 'd MMM yyyy');
}

function formatDateDots(value: string | Date) {
  const date = value instanceof Date ? value : new Date(value);
  return format(date, 'dd.MM.yyyy');
}

function formatNumericValue(value: number | null | undefined) {
  return value === null || value === undefined ? '-' : value.toString();
}

export function AssayChangeLogView() {
  const { data, isLoading, error } = useGet<AssayTmUpdatesResponse>('/assay-updates', {
    staleTime: 5 * 60 * 1000,
  });
  const { user } = useUser();
  const showThresholdColumns = hasFeature(user, Feature.RESEARCH);

  const updates = data?.updates ?? [];
  const changedDates = updates.map(u => new Date(u.changedAt));
  const firstAppliedAt = changedDates.length ? new Date(Math.min(...changedDates.map(d => d.getTime()))) : undefined;
  const mostRecentAt = changedDates.length ? new Date(Math.max(...changedDates.map(d => d.getTime()))) : undefined;
  // counts are intentionally not displayed in the intro copy

  if (error) {
    throw error;
  }

  if (isLoading) {
    return <LoadingView />;
  }

  return (
    <Box maxW="960px" margin="0 auto" padding={{ base: '24px 20px', md: '40px 32px' }}>
      <Heading size="lg">Resistomap Platform Quality Assurance Change Log</Heading>
      <Text marginTop="4" color="gray.600">
        This table represents the assay that have assay specific quality assurance criteria applied on the Resistomap
        Platform.
        {firstAppliedAt && mostRecentAt
          ? ` The first criteria were applied on ${formatDateDots(firstAppliedAt)} with the most recent updates on ${formatDateDots(
              mostRecentAt,
            )}.`
          : ''}{' '}
        Currently, details on assay specific criteria can be shared upon request.
      </Text>
      <Text marginTop="3" color="gray.600">
        Resistomap uses SmartChip qPCR with SYBR Green chemistry to quantify antimicrobial resistance (AMR) and other
        genes across large sample sets. Because SYBR Green does not distinguish target amplicons from non‑specific
        products, we manage specificity through careful primer design, adoption of community‑validated primer sets, and
        continuous in‑silico screening against up‑to‑date sequence databases.
      </Text>
      <Text marginTop="3" color="gray.600">
        Sample quality is strictly controlled for optimal performance of the SmartChip qPCR system. No template controls
        (NTCs) are used to separate true amplification from artefacts in complex environmental communities. The
        acceptance criteria for melting curve thresholds are applied to assays that have been identified to show a
        positive signal, such as primer‑dimer formation, also in the absence of a template. However, the environmental
        context is known to affect the results and the criteria are applied to serve variety of sample types coming from
        across the world. Resistomap’s internal database of more than 30 thousands environmental samples collected since
        2019 is used to aid and guide the continuous work for applying these criteria for better analysis service.
      </Text>
      <Text marginTop="3" color="gray.600">
        The Cq LOQ and assay specific copy numbers are used for the results labeled AY std. curve.
      </Text>

      {updates.length === 0 ? (
        <Text marginTop="10">No melting temperature changes have been recorded yet.</Text>
      ) : (
        <TableContainer marginTop="8">
          <Table variant="striped" colorScheme="gray" size="sm" width="100%">
            <Thead>
              <Tr>
                <Th>Assay</Th>
                <Th>Gene</Th>
                <Th>Last Changed</Th>
                {showThresholdColumns && <Th>Min Tm (°C)</Th>}
                {showThresholdColumns && <Th>Max Tm (°C)</Th>}
                {showThresholdColumns && <Th>Cq LOQ</Th>}
                <Th>
                  <Text>Assay specific</Text>
                  <Text>copy numbers</Text>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {updates.map(update => (
                <Tr key={update.assay}>
                  <Td>{update.assay}</Td>
                  <Td>{update.gene}</Td>
                  <Td>{formatDate(update.changedAt)}</Td>
                  {showThresholdColumns && <Td>{formatNumericValue(update.minMeltingTemperature)}</Td>}
                  {showThresholdColumns && <Td>{formatNumericValue(update.maxMeltingTemperature)}</Td>}
                  {showThresholdColumns && <Td>{formatNumericValue(update.loqCq)}</Td>}
                  <Td>{update.hasStandardCurve ? '✓' : ''}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
